/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import 'lazysizes';
import { gsap } from 'gsap';
import { debounce } from 'lodash-es';
import Vue from 'vue';
import Headroom from 'headroom.js';
// import Router from './router';
import { ScrollTrigger } from './gsap-bonus/ScrollTrigger';
import { SplitText } from './gsap-bonus/SplitText';

gsap.registerPlugin(ScrollTrigger);

require('./bootstrap');

require('./components/dropdown-component');
require('./components/video-component');
require('./components/faq-component');
require('./components/footer-menu-component');
require('./components/fortunior-form-component');
require('./components/load-filter-components');
require('./components/navigation-component');
require('./components/news-slider-component');
require('./components/fixtures-tables-component');
require('./components/players-slider-component');
require('./components/slider-component');
require('./components/reading-time-component');
require('./components/ranking-table-component');
require('./components/text-ticker-component');
require('./components/poll-component');
require('./components/image-grid-component');
require('./components/form-component');
require('./components/popup-component');
require('./components/lang-select');

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            router: {
                currentSection: null,
                instance: null,
                prefetch: true,
            },
            // router: null,
            // isMobile: false,
            // isTablet: false,
            // scrollbar: null,
            showNavigation: false,
            headroomHeader: null,
            // darkHeader: '1',
            // showFooter: '1'
        };
    },

    watch: {
        isMobile(value) {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet(value) {
            EventBus.$emit('is-tablet-changed');
        },
        showNavigation(visible) {
            EventBus.$emit('navigation-state-changed', visible);
        },
    },

    mounted() {
        this.initHeadroom();
        this.initRevealTriggers();
        this.initButtonHovers();

        window.addEventListener('resize', this.onWindowResize);
    },

    methods: {
        initHeadroom() {
            const options = {
                offset: 15,
                tolerance: 5,
            };
            this.header = document.querySelector('.header');
            this.headroomHeader = new Headroom(this.header, options);
            this.headroomHeader.init();
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('[data-fade-reveal]'),
                ...document.querySelectorAll('[data-image-reveal]'),
                ...document.querySelectorAll('[data-player-reveal]'),
            ];

            const splitEl = [
                ...document.querySelectorAll('[data-split-reveal]'),
            ];

            if (splitEl.length) {
                document.fonts.ready.then(() => {
                    this.initSplitRevealTriggers(splitEl);
                });
            }

            elements.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                const startPercentage = revealPosition || '90%';

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        if (!revealDelay) {
                            element.classList.add('--revealed');
                            return;
                        }

                        gsap.delayedCall(revealDelay, () => {
                            element.classList.add('--revealed');
                        });
                    },
                });
            });
        },

        initSplitRevealTriggers(items) {
            items.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                const startPercentage = revealPosition || '90%';

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        if (!revealDelay) {
                            const split = new SplitText(element, {
                                type: 'words, chars',
                                charsClass: 'char char++',
                                wordsClass: 'word-wrapper',
                            });
                            const { chars, words } = split;
                            const innerSplit = new SplitText(chars, {
                                type: 'chars',
                                charsClass: 'char-inner',
                            });
                            const charsInner = innerSplit.chars;

                            gsap.set(element, { autoAlpha: 1 });
                            gsap.from([words], {
                                duration: 1.2,
                                x: '-80',
                                ease: 'Power2.out',
                                onComplete: () => {
                                    innerSplit.revert();
                                    split.revert();
                                },
                            });
                            gsap.from([charsInner], {
                                duration: 0.6,
                                x: '-0.5em',
                                ease: 'Power3.out',
                            });
                            return;
                        }

                        gsap.delayedCall(revealDelay, () => {
                            const split = new SplitText(element, {
                                type: 'words,chars',
                                charsClass: 'char char++',
                                wordsClass: 'word-wrapper',
                            });
                            const { chars, words } = split;
                            const innerSplit = new SplitText(chars, {
                                type: 'chars',
                                charsClass: 'char-inner',
                            });
                            const charsInner = innerSplit.chars;

                            gsap.set(element, { autoAlpha: 1 });
                            gsap.from([words], {
                                duration: 1.2,
                                x: '-80',
                                ease: 'Power2.out',
                                onComplete: () => {
                                    innerSplit.revert();
                                    split.revert();
                                },
                            });
                            gsap.from([charsInner], {
                                duration: 0.6,
                                x: '-0.5em',
                                ease: 'Power3.out',
                            });
                        });
                    },
                });
            });
        },

        initButtonHovers() {
            const buttons = [
                ...document.querySelectorAll('.button'),
            ];

            buttons.forEach((button) => {
                let mouseOverComplete = false;
                let mouseLeft = false;
                button.addEventListener('mouseover', () => {
                    if (!mouseOverComplete && !mouseLeft) {
                        gsap.fromTo(button.querySelector('.button__bg-arrow'), {
                            x: '-100%',
                        }, {
                            x: '-40',
                            duration: 0.5,
                            ease: 'Power3.inOut',
                            onComplete: () => {
                                mouseOverComplete = true;
                                if (mouseLeft) {
                                    gsap.to(button.querySelector('.button__bg-arrow'), {
                                        x: '100%',
                                        duration: 0.3,
                                        ease: 'Power2.inOut',
                                        onComplete: () => {
                                            mouseOverComplete = false;
                                            mouseLeft = false;
                                        },
                                    });
                                }
                            },
                        });

                        if (button.classList.contains('--inverted')) {
                            gsap.to(button, {
                                color: '#ffffff',
                                duration: 0.5,
                                ease: 'Power3.inOut',
                                onComplete: () => {
                                    if (mouseLeft) {
                                        gsap.to(button, {
                                            color: '#00916f',
                                            duration: 0.2,
                                            ease: 'Power2.inOut',
                                        });
                                    }
                                },
                            });
                        }

                        if (button.classList.contains('--green')) {
                            gsap.to(button, {
                                color: '#00916f',
                                duration: 0.5,
                                ease: 'Power3.inOut',
                                onComplete: () => {
                                    if (mouseLeft) {
                                        gsap.to(button, {
                                            color: 'white',
                                            duration: 0.2,
                                            ease: 'Power2.inOut',
                                        });
                                    }
                                },
                            });
                        }

                        if (button.classList.contains('--yellow')) {
                            gsap.to(button, {
                                color: '#ffe21c',
                                duration: 0.5,
                                ease: 'Power3.inOut',
                                onComplete: () => {
                                    if (mouseLeft) {
                                        gsap.to(button, {
                                            color: '#00916f',
                                            duration: 0.2,
                                            ease: 'Power2.inOut',
                                        });
                                    }
                                },
                            });
                        }

                        if (button.classList.contains('--green-to-white')) {
                            gsap.to(button, {
                                color: '#00916f',
                                duration: 0.5,
                                ease: 'Power3.inOut',
                                onComplete: () => {
                                    if (mouseLeft) {
                                        gsap.to(button, {
                                            color: '#ffffff',
                                            duration: 0.2,
                                            ease: 'Power2.inOut',
                                        });
                                    }
                                },
                            });
                        }
                    }
                });

                button.addEventListener('mouseleave', () => {
                    mouseLeft = true;
                    if (mouseOverComplete) {
                        gsap.to(button.querySelector('.button__bg-arrow'), {
                            x: '100%',
                            duration: 0.3,
                            ease: 'Power2.inOut',
                            onComplete: () => {
                                mouseOverComplete = false;
                                mouseLeft = false;
                            },
                        });

                        if (button.classList.contains('--inverted')) {
                            gsap.to(button, {
                                color: '#00916f',
                                duration: 0.2,
                                ease: 'Power2.inOut',
                            });
                        }

                        if (button.classList.contains('--green')) {
                            gsap.to(button, {
                                color: 'white',
                                duration: 0.2,
                                ease: 'Power2.inOut',
                            });
                        }

                        if (button.classList.contains('--yellow')) {
                            gsap.to(button, {
                                color: '#00916f',
                                duration: 0.2,
                                ease: 'Power2.inOut',
                            });
                        }

                        if (button.classList.contains('--green-to-white')) {
                            gsap.to(button, {
                                color: 'white',
                                duration: 0.2,
                                ease: 'Power2.inOut',
                            });
                        }
                    }
                });
            });
        },

        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(() => {
            EventBus.$emit('window-resize');
        }, 150),

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
            console.log(`Is Mobile: ${this.isMobile}`);
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    },
});
